/* Ronaq — "design your own" configurator (dining set / niche). */
(function () {
  const GROUPS = {
    dining: [
      { key: 'size', ar: 'عدد الأفراد', en: 'Seating', kind: 'card' },
      { key: 'shape', ar: 'شكل الترابيزة', en: 'Table shape', kind: 'card' },
      { key: 'chair', ar: 'شكل الكرسي', en: 'Chair style', kind: 'chair', perChair: true },
      { key: 'fabric', ar: 'قماش الكراسي', en: 'Upholstery', kind: 'swatch', perChair: true },
      { key: 'wood', ar: 'لون الخشب', en: 'Wood finish', kind: 'swatch' },
      { key: 'top', ar: 'سطح الترابيزة', en: 'Table top', kind: 'card' },
      { key: 'accent', ar: 'لمسات معدنية', en: 'Metal accents', kind: 'swatch' },
    ],
    niche: [
      { key: 'size', ar: 'عرض النيش', en: 'Width', kind: 'card' },
      { key: 'crown', ar: 'شكل التاج', en: 'Crown style', kind: 'card' },
      { key: 'wood', ar: 'لون الخشب', en: 'Wood finish', kind: 'swatch' },
      { key: 'glass', ar: 'لون الزجاج', en: 'Glass', kind: 'swatch' },
      { key: 'handle', ar: 'المقابض', en: 'Handles', kind: 'swatch' },
      { key: 'light', ar: 'الإضاءة الداخلية', en: 'Interior lighting', kind: 'card' },
    ],
  };

  // which groups decide the big photo when nothing was picked yet
  const STAGE_PRIORITY = { dining: ['chair', 'shape', 'top'], niche: ['crown', 'light'] };
  // groups shown as small material chips over the big photo
  const CHIP_GROUPS = ['fabric', 'wood', 'top', 'accent', 'glass', 'handle'];

  const T = {
    ar: {
      perChair: 'للكرسي',
      included: 'مشمول',
      chairs: 'كراسي',
      currency: (n) => n.toLocaleString('en-US') + ' ج.م',
      waIntro: (shop) => `أهلاً ${shop}، عايز أطلب التصميم ده:`,
      total: 'الإجمالي التقديري',
      link: 'رابط التصميم',
      product: { dining: 'سفرة', niche: 'نيش' },
    },
    en: {
      perChair: 'per chair',
      included: 'Included',
      chairs: 'chairs',
      currency: (n) => 'EGP ' + n.toLocaleString('en-US'),
      waIntro: (shop) => `Hello ${shop}, I'd like to order this design:`,
      total: 'Estimated total',
      link: 'Design link',
      product: { dining: 'Dining set', niche: 'Niche' },
    },
  };

  class RonaqBuilder extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      const raw = this.querySelector('[data-rq-config]');
      if (!raw || !window.RonaqDraw) {
        // ronaq-draw.js may still be loading
        if (!this._retry) {
          this._retry = true;
          window.addEventListener('load', () => this.connectedCallback(), { once: true });
        }
        return;
      }
      this._ready = true;
      this.cfg = JSON.parse(raw.textContent);
      this.t = T[this.cfg.lang] || T.ar;
      this.groups = (GROUPS[this.cfg.mode] || GROUPS.dining)
        .map((g) => Object.assign({}, g, { items: this.cfg.choices.filter((c) => c.group === g.key) }))
        .filter((g) => g.items.length);
      this.sel = {};
      this.groups.forEach((g) => {
        const d = g.items.find((c) => c.isDefault) || g.items[0];
        this.sel[g.key] = d.id;
      });
      this.readHash();
      this.renderSteps();
      this.buildMini();
      this.update();
      this.bindOrder();
      this.initMini();
      this.init3D();
    }

    /* ----- live 3D model (falls back to photos when WebGL or the module is unavailable) ----- */
    init3D() {
      const start = () => {
        if (this.scene || !window.RonaqScene || !RonaqScene.supported()) return;
        const preview = this.querySelector('[data-rq-preview]');
        preview.innerHTML = '';
        preview.classList.remove('has-photo');
        preview.classList.add('is-3d');
        try {
          this.scene = RonaqScene.create(preview, this.cfg.mode, { grain: this.cfg.grain });
        } catch (e) {
          this.scene = null;
          preview.classList.remove('is-3d');
          this.update();
          return;
        }
        const ar = this.cfg.lang === 'ar';
        const hint = document.createElement('span');
        hint.className = 'rq-3d-hint';
        hint.innerHTML = `<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M21 12a9 9 0 1 1-3-6.7"/><path d="M21 4v5h-5"/></svg>${ar ? 'اسحب عشان تلف القطعة' : 'Drag to rotate'}`;
        preview.appendChild(hint);
        this.scene.interact(() => hint.classList.add('is-hidden'));
        const miniStage = this.mini && this.mini.querySelector('[data-mini-stage]');
        if (miniStage) miniStage.innerHTML = '';
        this.update();
      };
      if (window.RonaqScene) start();
      else window.addEventListener('ronaq3d:ready', start, { once: true });
    }

    sceneOpts() {
      const c = (k) => this.chosen(k) || {};
      const mat = (k) => ({ map: c(k).texture || null, color: c(k).color || null });
      if (this.cfg.mode === 'niche') {
        return {
          modules: parseInt(c('size').value, 10) || 3,
          crown: c('crown').value || 'flat',
          wood: mat('wood'),
          glass: c('glass').color || '#dfeef0',
          handle: c('handle').color || '#c4a46b',
          light: c('light').value === 'on',
        };
      }
      const acc = c('accent');
      return {
        seats: this.seats(),
        shape: c('shape').value || 'rect',
        chair: c('chair').value || 'classic',
        fabric: mat('fabric'),
        wood: mat('wood'),
        top: { type: c('top').value || 'wood', map: c('top').texture || null },
        accent: acc.value && acc.value !== 'none' ? acc.color : null,
      };
    }

    /* ----- floating mini preview (stays on screen while scrolling the options) ----- */
    buildMini() {
      const ar = this.cfg.lang === 'ar';
      const arrow = '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><path d="M9 6l6 6-6 6"/></svg>';
      const mini = document.createElement('div');
      mini.className = 'rq-mini';
      mini.setAttribute('aria-hidden', 'true');
      mini.inert = true;
      mini.innerHTML = `<button type="button" class="rq-mini__x" data-mini-close aria-label="${ar ? 'إخفاء' : 'Hide'}">${arrow}</button>
        <span class="rq-mini__stage" data-mini-stage role="button" tabindex="0" aria-label="${ar ? 'اعرض التصميم' : 'Show design'}"></span>
        <ul class="rq-chips rq-chips--mini" data-mini-chips></ul>
        <span class="rq-mini__cap"><small>${ar ? 'السعر التقديري' : 'Estimated'}</small><b data-rq-total>—</b></span>`;
      const tab = document.createElement('button');
      tab.type = 'button';
      tab.className = 'rq-minitab';
      tab.setAttribute('aria-hidden', 'true');
      tab.tabIndex = -1;
      tab.setAttribute('aria-label', ar ? 'اعرض التصميم' : 'Show design');
      tab.innerHTML = `<span class="rq-minitab__arrow">${arrow}</span><span class="rq-minitab__img" data-mini-tabimg></span>`;
      this.append(mini, tab);
      this.mini = mini;
      this.miniTab = tab;
    }

    initMini() {
      const mini = this.mini;
      const tab = this.miniTab;
      const canvas = this.querySelector('[data-rq-preview]');
      const steps = this.querySelector('[data-rq-steps]');
      const summary = this.querySelector('.rq-b__summary');
      const toStage = () => canvas.scrollIntoView({ behavior: 'smooth', block: 'center' });

      mini.addEventListener('click', (e) => {
        if (this.miniDragged) {
          this.miniDragged = false;
          e.preventDefault();
          return;
        }
        if (e.target.closest('[data-mini-close]')) {
          this.miniClosed = true;
          sync();
          return;
        }
        toStage();
      });
      mini.addEventListener('keydown', (e) => {
        if ((e.key === 'Enter' || e.key === ' ') && e.target.hasAttribute('data-mini-stage')) {
          e.preventDefault();
          toStage();
        }
      });
      tab.addEventListener('click', () => {
        this.miniClosed = false;
        sync();
      });

      let stageAbove = false;
      let stepsOn = false;
      let summaryOn = false;
      const sync = () => {
        const show = stageAbove && stepsOn && !summaryOn;
        const on = show && !this.miniClosed;
        mini.classList.toggle('is-visible', on);
        mini.setAttribute('aria-hidden', on ? 'false' : 'true');
        mini.inert = !on;
        if (this.scene) {
          if (on) this.scene.setHost(mini.querySelector('[data-mini-stage]'), false);
          else this.scene.setHost(canvas, true);
          if (show && this.miniClosed) {
            const img = this.scene.snapshot();
            this.miniTab.querySelector('[data-mini-tabimg]').innerHTML = `<img src="${img}" alt="" class="rq-photo">`;
          }
        }
        const tabOn = show && Boolean(this.miniClosed);
        tab.classList.toggle('is-visible', tabOn);
        tab.setAttribute('aria-hidden', tabOn ? 'false' : 'true');
        tab.tabIndex = tabOn ? 0 : -1;
        if (on) clearTimeout(this.clampTimer), (this.clampTimer = setTimeout(() => this.clampMini && this.clampMini(), 350));
      };
      if (!('IntersectionObserver' in window)) return;
      new IntersectionObserver(
        ([e]) => {
          stageAbove = !e.isIntersecting && e.boundingClientRect.top < 0;
          sync();
        },
        { threshold: 0.3 }
      ).observe(canvas);
      new IntersectionObserver(
        ([e]) => {
          stepsOn = e.isIntersecting;
          sync();
        },
        { rootMargin: '0px 0px -40% 0px' }
      ).observe(steps);
      new IntersectionObserver(
        ([e]) => {
          summaryOn = e.isIntersecting || e.boundingClientRect.top < 0;
          sync();
        },
        { rootMargin: '0px 0px -35% 0px' }
      ).observe(summary);
      this.initMiniDrag(mini);
    }

    /* drag the mini preview anywhere on screen; it never leaves the viewport */
    initMiniDrag(mini) {
      const KEY = 'rq-mini-pos';
      const GAP = 8;
      let pos = { x: 0, y: 0 };
      try {
        const saved = JSON.parse(sessionStorage.getItem(KEY) || 'null');
        if (saved && isFinite(saved.x) && isFinite(saved.y)) pos = saved;
      } catch (e) {}
      const floor = () => {
        const bar = this.querySelector('.rq-b__mobilebar');
        const vh = window.innerHeight;
        if (bar && getComputedStyle(bar).display !== 'none') return Math.min(vh, bar.getBoundingClientRect().top) - GAP;
        return vh - GAP;
      };
      const apply = () => (mini.style.translate = `${Math.round(pos.x)}px ${Math.round(pos.y)}px`);
      const clamp = () => {
        const r = mini.getBoundingClientRect();
        if (!r.width) return;
        const vw = document.documentElement.clientWidth;
        let dx = 0;
        let dy = 0;
        if (r.left < GAP) dx = GAP - r.left;
        else if (r.right > vw - GAP) dx = vw - GAP - r.right;
        if (r.top < GAP) dy = GAP - r.top;
        else if (r.bottom > floor()) dy = floor() - r.bottom;
        if (dx || dy) {
          pos = { x: pos.x + dx, y: pos.y + dy };
          apply();
        }
      };
      this.clampMini = clamp;
      apply();
      window.addEventListener('resize', clamp);

      let drag = null;
      mini.addEventListener('pointerdown', (e) => {
        if (e.pointerType === 'mouse' && e.button !== 0) return;
        if (e.target.closest('[data-mini-close]')) return;
        const r = mini.getBoundingClientRect();
        const vw = document.documentElement.clientWidth;
        drag = { id: e.pointerId, sx: e.clientX, sy: e.clientY, bx: pos.x, by: pos.y,
          minX: GAP - r.left, maxX: vw - GAP - r.right, minY: GAP - r.top, maxY: floor() - r.bottom, moved: false };
        this.miniDragged = false;
      });
      mini.addEventListener('pointermove', (e) => {
        if (!drag || e.pointerId !== drag.id) return;
        const dx = e.clientX - drag.sx;
        const dy = e.clientY - drag.sy;
        if (!drag.moved) {
          if (Math.hypot(dx, dy) < 6) return;
          drag.moved = true;
          try { mini.setPointerCapture(e.pointerId); } catch (err) {}
          mini.classList.add('is-dragging');
        }
        pos = {
          x: drag.bx + Math.max(drag.minX, Math.min(drag.maxX, dx)),
          y: drag.by + Math.max(drag.minY, Math.min(drag.maxY, dy)),
        };
        apply();
      });
      const end = (e) => {
        if (!drag || e.pointerId !== drag.id) return;
        if (drag.moved) {
          this.miniDragged = true;
          setTimeout(() => (this.miniDragged = false), 50);
          try { sessionStorage.setItem(KEY, JSON.stringify(pos)); } catch (err) {}
        }
        mini.classList.remove('is-dragging');
        drag = null;
      };
      mini.addEventListener('pointerup', end);
      mini.addEventListener('pointercancel', end);
    }

    name(c) {
      return this.cfg.lang === 'ar' ? c.name : c.nameEn || c.name;
    }
    chosen(key) {
      const g = this.groups.find((x) => x.key === key);
      return g ? g.items.find((c) => c.id === this.sel[key]) : null;
    }
    seats() {
      if (this.cfg.mode !== 'dining') return 1;
      const s = this.chosen('size');
      return Math.max(1, parseInt(s && s.value, 10) || 6);
    }
    money(n) {
      return this.t.currency(Math.round(n));
    }

    /* ----- selection <-> URL hash (shareable designs) ----- */
    readHash() {
      const m = location.hash.match(/rq=([\w,.-]+)/);
      if (!m) return;
      m[1].split(',').forEach((pair, i) => {
        const g = this.groups[i];
        const idx = parseInt(pair, 10);
        if (g && g.items[idx]) this.sel[g.key] = g.items[idx].id;
      });
    }
    writeHash() {
      const code = this.groups.map((g) => g.items.findIndex((c) => c.id === this.sel[g.key])).join(',');
      history.replaceState(null, '', location.pathname + location.search + '#rq=' + code);
    }
    shareUrl() {
      return location.href.split('#')[0] + location.hash;
    }

    /* ----- options UI ----- */
    renderSteps() {
      const host = this.querySelector('[data-rq-steps]');
      host.innerHTML = this.groups
        .map((g, i) => {
          const label = this.cfg.lang === 'ar' ? g.ar : g.en;
          return `<section class="rq-step" data-group="${g.key}">
            <h3 class="rq-step__title"><span class="rq-step__num">${i + 1}</span>${label}<em class="rq-step__picked" data-picked></em></h3>
            <div class="rq-opts rq-opts--${g.kind}" role="radiogroup" aria-label="${label}">
              ${g.items.map((c) => this.optionHtml(g, c)).join('')}
            </div>
          </section>`;
        })
        .join('');
      host.addEventListener('click', (e) => {
        const b = e.target.closest('[data-choice]');
        if (!b) return;
        const key = b.closest('[data-group]').dataset.group;
        this.sel[key] = b.dataset.choice;
        const picked = this.chosen(key);
        if (picked && picked.stage) this.stageKey = key;
        this.update();
        this.writeHash();
      });
    }

    priceTag(g, c) {
      if (!c.price) return `<span class="rq-opt__price rq-opt__price--free">${this.t.included}</span>`;
      const per = g.perChair ? ` <small>/ ${this.t.perChair}</small>` : '';
      return `<span class="rq-opt__price">+${this.money(c.price)}${per}</span>`;
    }

    optionHtml(g, c) {
      const nm = this.name(c);
      if (g.kind === 'swatch') {
        const none = !c.color || c.value === 'none';
        const bg = c.image ? `background-image:url('${c.image}')` : none ? '' : `--sw:${c.color}`;
        const look = c.image ? '' : none ? ' rq-swatch--none' : g.key === 'glass' ? ' rq-swatch--glass' : ' rq-swatch--metal';
        return `<button type="button" class="rq-opt rq-opt--swatch" data-choice="${c.id}" role="radio" title="${nm}">
          <span class="rq-swatch${look}" style="${bg}"></span>
          <span class="rq-opt__name">${nm}</span>${this.priceTag(g, c)}</button>`;
      }
      if (g.kind === 'chair') {
        return `<button type="button" class="rq-opt rq-opt--chair" data-choice="${c.id}" role="radio">
          ${c.image ? `<img src="${c.image}" alt="" loading="lazy" class="rq-opt__img rq-opt__img--chair">` : `<span class="rq-chair-icon" data-chair-icon="${c.value}"></span>`}
          <span class="rq-opt__name">${nm}</span>${this.priceTag(g, c)}</button>`;
      }
      return `<button type="button" class="rq-opt rq-opt--card" data-choice="${c.id}" role="radio">
        ${c.image ? `<img src="${c.image}" alt="" loading="lazy" class="rq-opt__img">` : ''}
        <span class="rq-opt__name">${nm}</span>${this.priceTag(g, c)}</button>`;
    }

    /* ----- preview + totals ----- */
    drawOpts() {
      const v = (k) => (this.chosen(k) || {}).value;
      const col = (k, d) => {
        const c = this.chosen(k);
        return (c && c.color) || d;
      };
      if (this.cfg.mode === 'niche') {
        return {
          modules: v('size') || 3,
          crown: v('crown') || 'flat',
          wood: col('wood', '#5b3a24'),
          glass: col('glass', '#dfeef0'),
          handle: col('handle', '#c4a46b'),
          light: v('light') === 'on',
        };
      }
      const acc = this.chosen('accent');
      return {
        seats: this.seats(),
        shape: v('shape') || 'rect',
        chair: v('chair') || 'classic',
        fabric: col('fabric', '#d8c6a8'),
        wood: col('wood', '#5b3a24'),
        top: v('top') || 'wood',
        accent: acc && acc.value !== 'none' ? acc.color : '',
      };
    }

    stagePhoto() {
      const fromKey = (k) => {
        const c = this.chosen(k);
        return c && c.stage ? c.stage : null;
      };
      if (this.stageKey && fromKey(this.stageKey)) return fromKey(this.stageKey);
      for (const k of STAGE_PRIORITY[this.cfg.mode] || []) if (fromKey(k)) return fromKey(k);
      return null;
    }

    /* swap the big picture with a short cross-fade (photos) or redraw (illustration) */
    showStage(host, photo, draw) {
      if (!photo) {
        host.classList.remove('has-photo');
        host.innerHTML = draw();
        return;
      }
      host.classList.add('has-photo');
      const cur = host.querySelector('img.rq-photo:last-child');
      if (cur && cur.getAttribute('src') === photo) return;
      const img = new Image();
      img.className = 'rq-photo is-entering';
      img.alt = '';
      img.decoding = 'async';
      img.src = photo;
      host.querySelectorAll(':scope > :not(img.rq-photo)').forEach((n) => n.remove());
      host.appendChild(img);
      const done = () => {
        requestAnimationFrame(() => img.classList.remove('is-entering'));
        setTimeout(() => host.querySelectorAll('img.rq-photo').forEach((n) => n !== img && n.remove()), 450);
      };
      if (img.complete) done();
      else img.addEventListener('load', done, { once: true }), img.addEventListener('error', done, { once: true });
    }

    chipsHtml() {
      return this.groups
        .filter((g) => CHIP_GROUPS.includes(g.key))
        .map((g) => {
          const c = this.chosen(g.key);
          if (!c || c.value === 'none') return '';
          const look = c.image ? '' : g.key === 'glass' ? ' rq-swatch--glass' : ' rq-swatch--metal';
          const st = c.image ? `background-image:url('${c.image}')` : `--sw:${c.color || '#ccc'}`;
          return `<li><span class="rq-swatch rq-swatch--chip${look}" style="${st}"></span>${this.name(c)}</li>`;
        })
        .join('');
    }

    total() {
      const seats = this.seats();
      return this.groups.reduce((sum, g) => {
        const c = this.chosen(g.key);
        return sum + (c ? (c.price || 0) * (g.perChair ? seats : 1) : 0);
      }, this.cfg.base || 0);
    }

    update() {
      const o = this.drawOpts();
      const preview = this.querySelector('[data-rq-preview]');
      const photo = this.stagePhoto();
      const draw = () =>
        photo
          ? `<img src="${photo}" alt="" class="rq-photo" decoding="async">`
          : this.cfg.mode === 'niche'
          ? RonaqDraw.niche(o)
          : RonaqDraw.dining(o);
      if (this.scene) this.scene.update(this.sceneOpts());
      else this.showStage(preview, photo, draw);
      const chips = this.chipsHtml();
      this.querySelector('[data-rq-chips]').innerHTML = chips;
      if (this.mini) {
        if (!this.scene) {
          this.showStage(this.mini.querySelector('[data-mini-stage]'), photo, draw);
          this.miniTab.querySelector('[data-mini-tabimg]').innerHTML = draw();
        }
        this.mini.querySelector('[data-mini-chips]').innerHTML = chips;
        this.mini.classList.remove('is-bump');
        void this.mini.offsetWidth;
        this.mini.classList.add('is-bump');
      }

      this.querySelectorAll('[data-group]').forEach((sec) => {
        const key = sec.dataset.group;
        sec.querySelectorAll('[data-choice]').forEach((b) => {
          const on = b.dataset.choice === this.sel[key];
          b.classList.toggle('is-active', on);
          b.setAttribute('aria-checked', on);
        });
        const c = this.chosen(key);
        sec.querySelector('[data-picked]').textContent = c ? this.name(c) : '';
      });
      this.querySelectorAll('[data-chair-icon]').forEach((el) => {
        el.innerHTML = RonaqDraw.chairIcon(el.dataset.chairIcon, o.fabric, o.wood);
      });

      const total = this.total();
      this.querySelectorAll('[data-rq-total]').forEach((el) => (el.textContent = this.money(total)));

      const seats = this.seats();
      const lines = this.groups.map((g) => {
        const c = this.chosen(g.key);
        const label = this.cfg.lang === 'ar' ? g.ar : g.en;
        let price = (c.price || 0) * (g.perChair ? seats : 1);
        let extra = g.perChair && c.price ? ` (${seats} × ${this.money(c.price)})` : '';
        return { label, value: this.name(c), price, extra };
      });
      this.lines = lines;
      this.querySelector('[data-rq-summary]').innerHTML = lines
        .map(
          (l) =>
            `<li><span>${l.label}</span><b>${l.value}</b><em>${l.price ? '+' + this.money(l.price) : this.t.included}</em></li>`
        )
        .join('');
      const body = this.querySelector('[data-rq-body]');
      if (body) body.value = this.orderText(false);
      const ti = this.querySelector('[data-rq-total-input]');
      if (ti) ti.value = this.money(total);
    }

    orderText(withIntro) {
      const t = this.t;
      const rows = this.lines.map((l) => `• ${l.label}: ${l.value}${l.extra}`).join('\n');
      return (
        (withIntro ? t.waIntro(this.cfg.shopName) + '\n\n' : '') +
        `${t.product[this.cfg.mode]}\n${rows}\n\n${t.total}: ${this.money(this.total())}\n${t.link}: ${this.shareUrl()}`
      );
    }

    bindOrder() {
      const wa = this.querySelector('[data-rq-wa]');
      if (wa) {
        wa.addEventListener('click', (e) => {
          e.preventDefault();
          const num = String(this.cfg.whatsapp || '').replace(/\D/g, '');
          const form = this.querySelector('.rq-b__form');
          const nm = form && form.querySelector('[name="contact[name]"]').value.trim();
          const city = form && form.querySelector('[name="contact[city]"]').value.trim();
          let txt = this.orderText(true);
          if (nm) txt += `\n\n${this.cfg.lang === 'ar' ? 'الاسم' : 'Name'}: ${nm}`;
          if (city) txt += `\n${this.cfg.lang === 'ar' ? 'المدينة' : 'City'}: ${city}`;
          window.open('https://wa.me/' + num + '?text=' + encodeURIComponent(txt), '_blank', 'noopener');
        });
      }
      const form = this.querySelector('.rq-b__form');
      if (form) {
        form.addEventListener('submit', () => {
          this.update();
          const email = form.querySelector('[data-rq-email]');
          // Shopify's contact form requires an email; most customers order by phone.
          if (email && !email.value.trim()) {
            const phone = form.querySelector('[name="contact[phone]"]').value.replace(/\D/g, '');
            email.value = `order-${phone || 'guest'}@${location.hostname.replace(/^www\./, "")}`;
          }
        });
      }
      if (location.search.includes('contact_posted=true')) {
        this.querySelector('.rq-b__summary').scrollIntoView({ block: 'start' });
      }
    }
  }

  if (!customElements.get('ronaq-builder')) customElements.define('ronaq-builder', RonaqBuilder);
})();
